// Next best action for renewals (Oct 2026) — the card on Now and on Renewals: for each renewal of the coming
// 60 days the one move worth making now, with the reason and the other signals; «Create task» makes it a task for
// today (deterministic id — never twice). Nothing is sent and nothing changes by itself. Logic: domain/renewal-actions.ts
import { useMemo, useState } from 'react';
import { createNextActionTask, renewalNextActions, type NextAction } from '@vero/domain';
import { useRuntime } from '../core/runtime.tsx';
import { Empty, PersonCell } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { navigate } from '../core/router.ts';
import { useShown } from './setup.tsx';

export function NextActionsCard({ limit = 5, personId, testId = 'next-actions' }: { limit?: number; personId?: string; testId?: string }) {
  const rt = useRuntime();
  const { t, i18n, view, today } = rt;
  const shown = useShown();
  const all = useMemo(() => renewalNextActions(view, today, { personId }).filter((a) => a.line === 'life' || shown('renewals:general')), [view, today, personId]);
  const [more, setMore] = useState(false);
  if (!all.length && !personId) return null;
  const list = more ? all : all.slice(0, limit);
  const params = (a: NextAction): Record<string, string | number> => ({ ...a.params, ...(typeof a.params.amount === 'number' ? { amount: i18n.money(a.params.amount) } : {}), ...(typeof a.params.date === 'string' ? { date: i18n.date(a.params.date) } : {}) });
  const make = (a: NextAction) => rt.run((v, c) => createNextActionTask(v, a, t('nba.task_title', { action: t(`nba.kind.${a.kind}`), product: a.policy.productName }), c), 'nba.task_done');
  return (
    <section className="card" aria-labelledby={`${testId}-title`} data-testid={testId}>
      <div className="card-head">
        <div><h2 id={`${testId}-title`}>{t('nba.title')}</h2><div className="small muted">{t('nba.subtitle')}</div></div>
        <span className="chip gold">{all.length}</span>
      </div>
      {list.length ? <ul className="nba-list" role="list">
        {list.map((a) => (
          <li key={a.id} className={`nba-item k-${a.kind}`} data-testid="nba-item">
            <span className="nba-icon" aria-hidden="true"><Icon name={ICON[a.kind] ?? 'bolt'} size={16} /></span>
            <div className="grow" style={{ minWidth: 0 }}>
              <div className="row wrap" style={{ gap: 8, alignItems: 'baseline' }}>
                <b>{t(`nba.kind.${a.kind}`)}</b>
                <span className="tiny muted">{a.policy.productName} · {t(a.days < 0 ? 'nba.expired' : 'nba.in_days', { days: Math.abs(a.days), date: i18n.date(a.dueDate, 'dayMonth') })}</span>
              </div>
              <div className="small">{t(`nba.why.${a.kind}`, params(a))}</div>
              <div className="row wrap" style={{ gap: 6, marginTop: 4 }}>
                {!personId && <PersonCell id={a.personId} />}
                {a.also.slice(0, 3).map((k) => <span key={k} className="chip">{t(`nba.also.${k}`)}</span>)}
              </div>
            </div>
            <div className="nba-actions">
              {a.taskExists ? <span className="chip ok"><Icon name="check" size={12} />{t('nba.task_exists')}</span>
                : <button className="btn btn-sm" onClick={() => void make(a)} data-testid="nba-task">{t('nba.create_task')}</button>}
              {a.line === 'general' && <button className="btn btn-sm btn-ghost" onClick={() => navigate('/renewals')}>{t('nav.renewals')}</button>}
            </div>
          </li>
        ))}
      </ul> : <Empty icon="check">{t('nba.empty')}</Empty>}
      {!more && all.length > limit && <div style={{ padding: '0 16px 14px' }}><button className="btn btn-ghost btn-sm" onClick={() => setMore(true)}>{t('nba.more', { count: all.length - limit })}</button></div>}
    </section>
  );
}
const ICON: Partial<Record<NextAction['kind'], string>> = {
  settle_arrears: 'coins', call_detractor: 'phone', resolve_service: 'shield', request_terms: 'mail', send_terms: 'mail', follow_up_terms: 'followup',
  confirm_renewal: 'check', explain_increase: 'insights', check_sum_insured: 'household', renew_kyc: 'lock', review_cover: 'reviews', confirm_contact: 'call', prepare_renewal: 'policy',
};
